import { useState, lazy, Suspense, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Award, Zap, Star, X } from 'lucide-react';
import { siteContent } from '../data/content';
import { expertiseData } from '../data/expertise';
import { healthPackagesData } from '../data/healthPackagesData';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import HomeHeroSkeleton from './HomeHeroSkeleton';
import './HomeOptimized.css';
import AutoSlider from "../components/AutoSlider";

// Lazy load heavy components
const Carousel = lazy(() => import('@/components/ui/carousel').then(module => ({ 
  default: module.Carousel 
})));
const CarouselContent = lazy(() => import('@/components/ui/carousel').then(module => ({ 
  default: module.CarouselContent 
})));
const CarouselItem = lazy(() => import('@/components/ui/carousel').then(module => ({ 
  default: module.CarouselItem 
})));
const CarouselPrevious = lazy(() => import('@/components/ui/carousel').then(module => ({ 
  default: module.CarouselPrevious 
})));
const CarouselNext = lazy(() => import('@/components/ui/carousel').then(module => ({ 
  default: module.CarouselNext 
})));

const Home = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [selectedExpertise, setSelectedExpertise] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [isHeroLoaded, setIsHeroLoaded] = useState(false);
  const [isAboveFoldLoaded, setIsAboveFoldLoaded] = useState(false);
  const iconMap = { Shield, Clock, Award, Zap };

  const featuredExpertise = expertiseData.areas.slice(0, 3);

  // Preload critical images
  useEffect(() => {
    // Preload hero image
    const heroImage = new Image();
    heroImage.src = process.env.PUBLIC_URL + "/images/Post011.png";
    heroImage.onload = () => {
      setIsHeroLoaded(true);
    };

    // Mark above-fold content as loaded after critical images
    setTimeout(() => {
      setIsAboveFoldLoaded(true);
    }, 100);

    // Preload other critical assets
    const preloadImages = [
      "https://images.pexels.com/photos/7088526/pexels-photo-7088526.jpeg"
    ];
    
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    // Preload carousel images
    siteContent.about.images.forEach(image => {
      const img = new Image();
      img.src = image;
    });

    // Preload service images
    siteContent.services.slice(0, 3).forEach(service => {
      const img = new Image();
      img.src = service.image;
    });

    // Preload health package images
    healthPackagesData.slice(0, 2).forEach(pkg => {
      const img = new Image();
      img.src = pkg.image;
    });
  }, []);

  // Optimized Hero Image Component
  const HeroImage = () => (
    <div className="relative slide-in-right hidden lg:block">
      {!isHeroLoaded && (
        <div className="w-full h-[500px] bg-gradient-to-r from-green-200 to-emerald-200 animate-pulse rounded-3xl"></div>
      )}
      <img
        src={process.env.PUBLIC_URL + "/images/Post011.png"}
        alt="Medical Equipment"
        className={`rounded-3xl shadow-2xl w-full h-[500px] object-cover transition-opacity duration-700 ${
          isHeroLoaded ? 'opacity-100' : 'opacity-0 absolute'
        }`}
        loading="eager"
        fetchpriority="high"
      />
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Critical Above-Fold Content - No lazy loading here */}
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-green-50 via-white to-green-100">
  {/* Background blobs */}
  <div className="absolute inset-0 z-0">
    <div className="absolute top-20 left-10 w-72 h-72 bg-green-800 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
    <div
      className="absolute bottom-20 right-10 w-96 h-96 bg-green-900 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"
      style={{ animationDelay: "2s" }}
    ></div>
  </div>

  <div className="container mx-auto px-4 z-10">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      
      {/* LEFT CONTENT (shifted up) */}
      <div
        className={`fade-in ${
          isAboveFoldLoaded ? "opacity-100" : "opacity-0"
        } -mt-12 lg:-mt-20`}
      >
        {/* Logo */}
        <div className="mb-0 ml-4">
          <div className="w-44 h-44 sm:w-48 sm:h-48 md:w-56 md:h-56">
            <img
              src={process.env.PUBLIC_URL + "/images/logo2.png"}
              alt="Surya Diagnostic Center Logo"
              className="w-full h-full object-contain"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </div>

        {/* Marathi tagline */}
        <p className="ml-4 mb-4 text-sm sm:text-base md:text-lg font-medium tracking-wide text-gray-800">
          उच्च तंत्रज्ञान | अनुभवी डॉक्टर | अचूक निदान
        </p>

        {/* Title */}
        <h1 className="text-5xl sm:text-6xl lg:text-6xl font-bold text-gray-900 mb-3 leading-tight ml-4">
          {siteContent.hero.title}
        </h1>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-gray-600 mb-5 leading-relaxed ml-4">
          {siteContent.hero.subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4 mb-6 ml-4">
          <Link
            to="/contact"
            className="px-8 py-4 bg-gradient-to-r from-green-900 to-green-900 text-white rounded-full font-semibold hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2"
          >
            {siteContent.hero.cta}
            <ArrowRight size={20} />
          </Link>

          <Link
            to="/services"
            className="px-8 py-4 bg-white text-green-800 border-2 border-green-800 rounded-full font-semibold hover:bg-green-800 hover:text-white transition-all"
          >
            Our Services
          </Link>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 ml-4">
          {siteContent.hero.features.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <div key={index} className="flex items-center gap-2 text-gray-700">
                {Icon && <Icon size={20} className="text-green-600" />}
                <span className="text-sm font-medium">{feature.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT IMAGE (pushed slightly down) */}
      <div className="mt-10 lg:mt-2">
        <HeroImage />
      </div>
    </div>
  </div>
</section>


      {/* About Section with Lazy Loaded Carousel */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {siteContent.about.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {siteContent.about.description}
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                {siteContent.about.stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
                    <div className="text-3xl font-bold text-green-600 mb-2">{stat.number}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-green-600 font-semibold hover:gap-4 transition-all"
                data-testid="about-learn-more"
              >
                Learn More About Us
                <ArrowRight size={20} />
              </Link>
            </div>
            
            {/* Lazy loaded carousel */}
            <div className="slide-in-right">
              <Suspense fallback={
                <div className="w-full aspect-[4/3] sm:aspect-[16/10] object-contain sm:object-cover rounded-3xl shadow-xl bg-white">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div>
                </div>
              }>
                <Carousel
                  className="w-full"
                  opts={{
                    loop: true,
                  }}
                >
                  <CarouselContent>
                    {siteContent.about.images.map((image, index) => (
                     <CarouselItem key={index}>
  <div className="w-full h-[300px] sm:h-[400px] lg:h-[500px] flex items-center justify-center bg-gray-50 rounded-3xl overflow-hidden shadow-xl">
    <img
      src={image}
      alt={`About ${index + 1}`}
      className="max-h-full max-w-full object-contain sm:object-cover w-full"
      loading="lazy"
      decoding="async"
    />
  </div>
</CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-4" />
                  <CarouselNext className="right-4" />
                </Carousel>
              </Suspense>
            </div>
          </div>
        </div>
      </section>

                         <AutoSlider />


      {/* Services Section */}
      <section className="section-padding bg-gradient-to-br from-green-50 to-green-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive diagnostic services with state-of-the-art technology
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {siteContent.services.map((service, index) => (
              <Card
                key={service.id}
                className="card-hover bg-white p-8 rounded-3xl cursor-pointer group"
                onClick={() => setSelectedService(service)}
                style={{animationDelay: `${index * 0.1}s`}}
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.name} 
                    className="w-full h-full object-cover rounded-2xl"
                    loading={index < 3 ? "eager" : "lazy"}
                    decoding={index < 3 ? "sync" : "async"}
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                  {service.name}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link
                  to={`/services/${service.id}`}
                  className="text-green-600 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  Know More
                  <ArrowRight size={18} />
                </Link>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-full font-semibold hover:shadow-2xl hover:scale-105 transition-all"
              data-testid="view-all-services"
            >
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise Section - Lazy load image */}
      <section className="section-padding bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {expertiseData.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">{expertiseData.subtitle}</p>
              <p className="text-gray-700 mb-8 text-lg">
                Expertise in Advanced Imaging Scans in all Radiography tests like 2D Echo, Fetal Echo, Doppler, Anomaly Scan, NT Scan, Obstetrics Doppler, CT Angiography, and more.
              </p>
              <div className="mb-8">
                <h3 className="text-xl font-bold text-green-800 mb-4">Advanced Imaging Technologies:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700">CT SCAN</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700">SONOGRAPHY</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700">DIGITAL X-RAY</span>
                  </div>
                </div>
              </div>
              <Link
                to="/expertise"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all"
              >
                Explore All Expertise
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="slide-in-right">
              <img
                src="https://images.pexels.com/photos/7088526/pexels-photo-7088526.jpeg"
                alt="Advanced Medical Imaging"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          {/* Featured Expertise Cards */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Key Expertise Areas</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {featuredExpertise.map((expertise, index) => (
                <Card
                  key={expertise.id}
                  className="card-hover bg-white p-6 rounded-2xl cursor-pointer group border-2 border-transparent hover:border-green-500 transition-all"
                  onClick={() => setSelectedExpertise(expertise)}
                  data-testid={`expertise-card-${index}`}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <div className="text-white text-xl font-bold">+</div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                    {expertise.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{expertise.description}</p>
                  <button className="text-green-600 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all text-sm">
                    Learn More
                    <ArrowRight size={16} />
                  </button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Health Packages Section - Lazy load images */}
      <section className="section-padding bg-gradient-to-br from-green-50 to-green-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Our Health Packages</h2>
            <p className="text-lg text-gray-600">Comprehensive health checkups tailored to your needs</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {healthPackagesData.map((pkg, index) => (
              <Card
                key={pkg.id}
                className="card-hover bg-white p-8 rounded-3xl"
                style={{animationDelay: `${index * 0.1}s`}}
                data-testid={`package-card-${pkg.id}`}
              >
                <div className="mb-6">
                  <img 
                    src={pkg.image} 
                    alt={pkg.name} 
                    className="w-full h-48 object-cover rounded-2xl"
                    loading={index < 2 ? "eager" : "lazy"}
                    decoding={index < 2 ? "sync" : "async"}
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{pkg.name}</h3>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <Link
                  to={`/health-packages/${pkg.id}`}
                  className="inline-flex items-center gap-2 text-green-600 font-semibold hover:gap-4 transition-all"
                >
                  View Details
                  <ArrowRight size={18} />
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Add your remaining sections here */}
      {/* Why Choose Us, Testimonials, Director's Message, etc. */}

      {/* Modals */}
      {selectedService && (
        <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{selectedService.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <img 
                src={selectedService.image} 
                alt={selectedService.name} 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-gray-700">{selectedService.description}</p>
              <Link
                to={`/services/${selectedService.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
              >
                View Full Details
                <ArrowRight size={18} />
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {selectedExpertise && (
        <Dialog open={!!selectedExpertise} onOpenChange={() => setSelectedExpertise(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{selectedExpertise.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700">{selectedExpertise.description}</p>
              <div className="grid grid-cols-2 gap-3">
                {selectedExpertise.technologies?.map((tech, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700">{tech}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/expertise"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
              >
                Explore All Expertise
                <ArrowRight size={18} />
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Home;